import React, { useEffect, useState } from 'react';
import './App.css';

function App() {
  const [expandedService, setExpandedService] = useState<number | null>(null);
  const [expandedBlogPost, setExpandedBlogPost] = useState<number | null>(null);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [showBackToTop, setShowBackToTop] = useState<boolean>(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowBackToTop(true);
      } else {
        setShowBackToTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const toggleService = (id: number) => {
    if (expandedService === id) {
      setExpandedService(null);
    } else {
      setExpandedService(id);
    }
  };

  const toggleBlogPost = (id: number) => {
    if (expandedBlogPost === id) {
      setExpandedBlogPost(null);
    } else {
      setExpandedBlogPost(id);
    }
  };

  const toggleFaq = (id: number) => {
    if (expandedFaq === id) {
      setExpandedFaq(null);
    } else {
      setExpandedFaq(id);
    }
  };

  return (
    <div className="App">
      {/* Top Banner */}
      <div className="top-banner">
        <div className="top-banner-brand">
          <span>We</span>Do<span>Certs</span>
        </div>
        <div className="top-banner-phone">
          <div className="top-banner-phone-icon">📞</div>
          <a href="tel:01217512229">0121 751 2229</a>
        </div>
      </div>
      
      {/* Hero Section */}
      <section id="hero">
        <div className="hero-container">
          <div className="hero-content">
            <h1 className="hero-title">
              <span className="text-brand-green">We</span>Do<span className="text-brand-green">Certs</span>
            </h1>
            <h2 className="hero-subtitle">Property Compliance Certificates from £59</h2>
            <p className="hero-description">
              At We Do Certs, we're all about making property compliance simple, fast, and stress-free. We specialise in delivering fully accredited certification services that keep your properties safe, legal, and ready for sale or rent.
            </p>
            <div className="hero-services">
              <h3>Key services:</h3>
              <ul className="hero-services-list">
                <li><span className="check-icon">✓</span> Gas Safety Certificates</li>
                <li><span className="check-icon">✓</span> Electrical Safety Certificates (EICR)</li>
                <li><span className="check-icon">✓</span> Energy Performance Certificates (EPC)</li>
              </ul>
            </div>
            <div className="hero-cta">
              <a href="#contact" className="hero-cta-button">Call us on 0121 751 2229</a>
            </div>
          </div>
          <div className="hero-image">
            <img src="/images/property-compliance-hero.jpg" alt="Property compliance services" />
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services">
        <div className="services-container">
          <div className="services-header">
            <h2 className="services-title">Our Services</h2>
            <p className="services-description">
              Whether you're a landlord, property manager, estate agent, or homeowner, we provide everything you need to stay compliant in one place—including:
            </p>
          </div>
          <div className="services-grid">
            <div className="service-card">
              <div className="card-icon">✓</div>
              <div className="card-content">
                <h3>Gas Safety Certificates</h3>
                <p>Annual gas safety checks required for all rental properties with gas appliances.</p>
                <button className="expand-button" onClick={() => toggleService(1)}>
                  Learn more <span className="expand-icon">{expandedService === 1 ? '↑' : '→'}</span>
                </button>
                {expandedService === 1 && (
                  <div className="expanded-content">
                    <p>Our Gas Safe registered engineers conduct thorough inspections of all gas appliances, pipework, and flues to ensure they meet safety standards. The certificate is valid for 12 months and is legally required for all rental properties.</p>
                  </div>
                )}
              </div>
            </div>
            <div className="service-card">
              <div className="card-icon">✓</div>
              <div className="card-content">
                <h3>Electrical Safety Certificates (EICR)</h3>
                <p>Mandatory electrical safety reports for rental properties, valid for 5 years.</p>
                <button className="expand-button" onClick={() => toggleService(2)}>
                  Learn more <span className="expand-icon">{expandedService === 2 ? '↑' : '→'}</span>
                </button>
                {expandedService === 2 && (
                  <div className="expanded-content">
                    <p>Our qualified electricians perform comprehensive inspections of your property's electrical installations to identify any potential hazards. The EICR is now mandatory for all rental properties and must be renewed every 5 years.</p>
                  </div>
                )}
              </div>
            </div>
            <div className="service-card">
              <div className="card-icon">✓</div>
              <div className="card-content">
                <h3>Energy Performance Certificates (EPC)</h3>
                <p>Required for all properties being sold or rented, valid for 10 years.</p>
                <button className="expand-button" onClick={() => toggleService(3)}>
                  Learn more <span className="expand-icon">{expandedService === 3 ? '↑' : '→'}</span>
                </button>
                {expandedService === 3 && (
                  <div className="expanded-content">
                    <p>Our accredited domestic energy assessors evaluate your property's energy efficiency and provide recommendations for improvements. EPCs are legally required when selling or renting a property and are valid for 10 years.</p>
                  </div>
                )}
              </div>
            </div>
            <div className="service-card">
              <div className="card-icon">✓</div>
              <div className="card-content">
                <h3>Asbestos Surveys & Certificates</h3>
                <p>Essential for older properties to identify and manage asbestos-containing materials.</p>
                <button className="expand-button" onClick={() => toggleService(4)}>
                  Learn more <span className="expand-icon">{expandedService === 4 ? '↑' : '→'}</span>
                </button>
                {expandedService === 4 && (
                  <div className="expanded-content">
                    <p>Our certified asbestos surveyors conduct thorough inspections to identify any asbestos-containing materials in your property. This is particularly important for buildings constructed before 2000 and is essential for compliance with the Control of Asbestos Regulations.</p>
                  </div>
                )}
              </div>
            </div>
            <div className="service-card">
              <div className="card-icon">✓</div>
              <div className="card-content">
                <h3>Fire Risk Assessments</h3>
                <p>Comprehensive fire safety evaluations for rental properties and HMOs.</p>
                <button className="expand-button" onClick={() => toggleService(5)}>
                  Learn more <span className="expand-icon">{expandedService === 5 ? '↑' : '→'}</span>
                </button>
                {expandedService === 5 && (
                  <div className="expanded-content">
                    <p>Our fire safety experts conduct detailed assessments to identify potential fire hazards and recommend appropriate safety measures. This is a legal requirement for landlords under the Regulatory Reform (Fire Safety) Order 2005, especially for HMOs and multi-occupancy buildings.</p>
                  </div>
                )}
              </div>
            </div>
            <div className="service-card">
              <div className="card-icon">✓</div>
              <div className="card-content">
                <h3>Emergency Lighting Reports</h3>
                <p>Ensuring emergency lighting systems are fully functional and compliant.</p>
                <button className="expand-button" onClick={() => toggleService(6)}>
                  Learn more <span className="expand-icon">{expandedService === 6 ? '↑' : '→'}</span>
                </button>
                {expandedService === 6 && (
                  <div className="expanded-content">
                    <p>Our qualified technicians test and certify emergency lighting systems to ensure they provide adequate illumination during power failures or emergencies. This service is essential for compliance with fire safety regulations in multi-occupancy buildings.</p>
                  </div>
                )}
              </div>
            </div>
            <div className="service-card">
              <div className="card-icon">✓</div>
              <div className="card-content">
                <h3>Fire Safety & Smoke Alarm Compliance</h3>
                <p>Installation and certification of smoke and carbon monoxide alarms.</p>
                <button className="expand-button" onClick={() => toggleService(7)}>
                  Learn more <span className="expand-icon">{expandedService === 7 ? '↑' : '→'}</span>
                </button>
                {expandedService === 7 && (
                  <div className="expanded-content">
                    <p>We install, inspect, and certify smoke and carbon monoxide alarms to ensure they meet current regulations. All rental properties must have working smoke alarms on each floor and carbon monoxide detectors in rooms with solid fuel burning appliances.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="additional-services">
            <h3 className="additional-services-title">We also offer:</h3>
            <div className="additional-services-grid">
              <div className="additional-service-item">
                <span className="check-icon">✓</span> Building Regulations Drawings
              </div>
              <div className="additional-service-item">
                <span className="check-icon">✓</span> Planning Application Drawings
              </div>
              <div className="additional-service-item">
                <span className="check-icon">✓</span> Simple Floor plans
              </div>
              <div className="additional-service-item">
                <span className="check-icon">✓</span> Detailed floor plans
              </div>
              <div className="additional-service-item">
                <span className="check-icon">✓</span> 3D Floor plans
              </div>
              <div className="additional-service-item">
                <span className="check-icon">✓</span> Virtual Staging
              </div>
              <div className="additional-service-item">
                <span className="check-icon">✓</span> Virtual photography
              </div>
            </div>
          </div>
          <div className="services-footer">
            <p className="services-footer-text">Helping you plan, renovate, or market your property with confidence and clarity.</p>
            <a href="#contact" className="services-cta-button">Get Started Today</a>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-section">
        <div className="geometric-patterns">
          <div className="geometric-shape shape-circle" style={{ width: '200px', height: '200px', top: '10%', left: '5%' }}></div>
          <div className="geometric-shape shape-circle" style={{ width: '150px', height: '150px', bottom: '15%', right: '10%' }}></div>
          <div className="geometric-shape shape-circle" style={{ width: '100px', height: '100px', top: '40%', right: '20%' }}></div>
        </div>
        <div className="section-content">
          <div className="section-heading">
            <h2>Why Choose We Do Certs?</h2>
            <div className="heading-underline"></div>
          </div>
          <div className="selling-points-grid">
            <div className="selling-point">
              <div className="why-choose-checkmark">✓</div>
              <div className="why-choose-icon">🛡️</div>
              <h3 className="selling-point-title">All-in-One Compliance Solution</h3>
              <p className="selling-point-description">Get all your property compliance certificates from a single trusted provider, saving you time and hassle.</p>
            </div>
            <div className="selling-point">
              <div className="why-choose-checkmark">✓</div>
              <div className="why-choose-icon">🏆</div>
              <h3 className="selling-point-title">Fully Qualified, Accredited Professionals</h3>
              <p className="selling-point-description">Our team consists of certified experts who maintain the highest industry standards and stay current with regulatory changes.</p>
            </div>
            <div className="selling-point">
              <div className="why-choose-checkmark">✓</div>
              <div className="why-choose-icon">⏱️</div>
              <h3 className="selling-point-title">Fast Turnaround Times</h3>
              <p className="selling-point-description">We understand urgency—most certificates are delivered within 72 hours of your booking.</p>
            </div>
            <div className="selling-point">
              <div className="why-choose-checkmark">✓</div>
              <div className="why-choose-icon">💰</div>
              <h3 className="selling-point-title">Competitive Pricing – No Hidden Costs</h3>
              <p className="selling-point-description">Clear, upfront pricing with no surprise fees or hidden charges. What we quote is what you pay.</p>
            </div>
            <div className="selling-point">
              <div className="why-choose-checkmark">✓</div>
              <div className="why-choose-icon">🌍</div>
              <h3 className="selling-point-title">Nationwide Coverage</h3>
              <p className="selling-point-description">Our network of professionals covers the entire UK, ensuring you get the service you need wherever your property is located.</p>
            </div>
            <div className="selling-point">
              <div className="why-choose-checkmark">✓</div>
              <div className="why-choose-icon">👥</div>
              <h3 className="selling-point-title">Friendly, Knowledgeable Service</h3>
              <p className="selling-point-description">Our team doesn't just process certificates—we provide guidance and answer your questions every step of the way.</p>
            </div>
          </div>
          <p className="closing-paragraph">
            We understand the pressure that comes with meeting property regulations—so we've built a service that's fast, reliable, and built around your needs. At We Do Certs, your compliance is our priority.
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="benefits-content">
          <div className="section-heading">
            <h2>How Will It Benefit Me?</h2>
            <div className="heading-underline"></div>
          </div>
          <div className="benefits-container">
            <div className="benefit-container even">
              <div className="benefit-icon-wrapper">
                <div className="benefit-checkmark">✓</div>
                <div className="benefit-icon">⏰</div>
              </div>
              <div className="benefit-content">
                <h3 className="benefit-title">Save Time</h3>
                <p className="benefit-description">No more chasing multiple companies or juggling appointments. With We Do Certs, one call covers it all—gas, electric, EPCs, fire safety, asbestos, and more. We streamline everything so you can focus on what matters.</p>
                <button className="expand-button" onClick={() => toggleService(1)}>
                  Learn more <span className="expand-icon">{expandedService === 1 ? '↑' : '→'}</span>
                </button>
              </div>
            </div>
            <div className="benefit-container odd">
              <div className="benefit-icon-wrapper">
                <div className="benefit-checkmark">✓</div>
                <div className="benefit-icon">📅</div>
              </div>
              <div className="benefit-content">
                <h3 className="benefit-title">Simplified Scheduling</h3>
                <p className="benefit-description">Need multiple certificates? We'll coordinate inspections to happen together, reducing disruption to your day or tenant inconvenience.</p>
                <button className="expand-button" onClick={() => toggleService(2)}>
                  Learn more <span className="expand-icon">{expandedService === 2 ? '↑' : '→'}</span>
                </button>
              </div>
            </div>
            <div className="benefit-container even">
              <div className="benefit-icon-wrapper">
                <div className="benefit-checkmark">✓</div>
                <div className="benefit-icon">👤</div>
              </div>
              <div className="benefit-content">
                <h3 className="benefit-title">One Point of Contact</h3>
                <p className="benefit-description">You'll deal with one trusted team who knows your property and your needs. No repeating yourself or managing different providers—just seamless, professional service from start to finish.</p>
                <button className="expand-button" onClick={() => toggleService(3)}>
                  Learn more <span className="expand-icon">{expandedService === 3 ? '↑' : '→'}</span>
                </button>
              </div>
            </div>
            <div className="benefit-container odd">
              <div className="benefit-icon-wrapper">
                <div className="benefit-checkmark">✓</div>
                <div className="benefit-icon">💰</div>
              </div>
              <div className="benefit-content">
                <h3 className="benefit-title">Cost-Effective Packages</h3>
                <p className="benefit-description">Bundled services mean better value. Our combined compliance packages are designed to save you money compared to booking each service separately.</p>
                <button className="expand-button" onClick={() => toggleService(4)}>
                  Learn more <span className="expand-icon">{expandedService === 4 ? '↑' : '→'}</span>
                </button>
              </div>
            </div>
          </div>
          <div className="closing-tagline">
            <div className="tagline-highlight">Complete Compliance, Complete Peace of Mind</div>
            <p className="tagline-subtext">With We Do Certs, you're not just ticking boxes—you're ensuring your properties are safe, legal, and ready for whatever comes next.</p>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="testimonials-container">
          <div className="testimonials-heading">
            <h2>What Our Customers Say</h2>
            <div className="heading-underline"></div>
            <p>Don't just take our word for it—here's what property owners and managers have to say about our services.</p>
          </div>
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="customer-type-badge badge-landlord">Landlord</div>
              <div className="quote-mark">"</div>
              <div className="testimonial-content">
                <div className="testimonial-rating">
                  <span className="star">★</span>
                  <span className="star">★</span>
                  <span className="star">★</span>
                  <span className="star">★</span>
                  <span className="star">★</span>
                </div>
                <p className="testimonial-quote">I manage 12 rental properties and used to spend days coordinating different inspections. We Do Certs has simplified everything—one call, one team, all certificates sorted. Absolute game-changer for landlords.</p>
              </div>
              <div className="customer-info">
                <div className="customer-avatar">JT</div>
                <div>
                  <div className="customer-name">James T.</div>
                  <div className="customer-role">Portfolio Landlord</div>
                </div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="customer-type-badge badge-investor">Property Investor</div>
              <div className="quote-mark">"</div>
              <div className="testimonial-content">
                <div className="testimonial-rating">
                  <span className="star">★</span>
                  <span className="star">★</span>
                  <span className="star">★</span>
                  <span className="star">★</span>
                  <span className="star">★</span>
                </div>
                <p className="testimonial-quote">When you're flipping multiple properties, time is money. The team at We Do Certs understands this perfectly. Their bundled services and quick turnaround have saved me thousands. For anyone managing multiple properties, this service is a game-changer.</p>
              </div>
              <div className="customer-info">
                <div className="customer-avatar">SM</div>
                <div>
                  <div className="customer-name">Sarah M.</div>
                  <div className="customer-role">Property Developer</div>
                </div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="customer-type-badge badge-homeowner">Homeowner</div>
              <div className="quote-mark">"</div>
              <div className="testimonial-content">
                <div className="testimonial-rating">
                  <span className="star">★</span>
                  <span className="star">★</span>
                  <span className="star">★</span>
                  <span className="star">★</span>
                  <span className="star">★</span>
                </div>
                <p className="testimonial-quote">I was impressed by how easy the process was. I needed an EPC, EICR, and a Gas Safety Certificate urgently, and they delivered everything within 48 hours after inspection. Highly recommended!</p>
              </div>
              <div className="customer-info">
                <div className="customer-avatar">AP</div>
                <div>
                  <div className="customer-name">Amrita P.</div>
                  <div className="customer-role">Homeowner</div>
                </div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="customer-type-badge badge-agency">Estate Agency</div>
              <div className="quote-mark">"</div>
              <div className="testimonial-content">
                <div className="testimonial-rating">
                  <span className="star">★</span>
                  <span className="star">★</span>
                  <span className="star">★</span>
                  <span className="star">★</span>
                  <span className="star">★</span>
                </div>
                <p className="testimonial-quote">We Do Certs saved us so much time and hassle. Having one point of contact for everything from fire risk assessments to architectural floor plans made the whole process seamless.</p>
              </div>
              <div className="customer-info">
                <div className="customer-avatar">AB</div>
                <div>
                  <div className="customer-name">Asad B.</div>
                  <div className="customer-role">Estate Agency Manager</div>
                </div>
              </div>
            </div>
          </div>
          <div className="testimonials-cta">
            <a href="#contact" className="cta-button">Join Our Satisfied Customers</a>
          </div>
        </div>
      </section>

      {/* Blog/Knowledge Hub Section */}
      <section className="blog-section">
        <div className="blog-container">
          <div className="blog-header">
            <h2 className="blog-title">Knowledge Hub</h2>
            <div className="blog-underline"></div>
          </div>
          <div className="blog-posts-container">
            <div className="blog-post">
              <div className="blog-post-inner">
                <div className="blog-post-header">
                  <div className="blog-post-icon">🏠</div>
                  <h3 className="blog-post-title">Landlord Legal Requirements for 2025: Why Property Compliance Is More Important Than Ever</h3>
                </div>
                <p className="blog-post-excerpt">
                  With regulations tightening and penalties increasing, staying compliant has never been more critical for landlords. Here's what you need to know about the latest changes.
                </p>
                <button className="blog-expand-btn" onClick={() => toggleBlogPost(1)}>
                  Read more <span className={`blog-expand-icon ${expandedBlogPost === 1 ? 'rotate' : ''}`}>↓</span>
                </button>
                <div className={`blog-content ${expandedBlogPost === 1 ? '' : 'blog-content-hidden'}`}>
                  <p>The landscape of property compliance is constantly evolving, with 2025 bringing significant changes that landlords need to be aware of. From stricter energy efficiency requirements to enhanced electrical safety standards, the regulatory framework continues to tighten.</p>
                  
                  <h4>Key Changes for 2025:</h4>
                  <ul>
                    <li><strong>Minimum Energy Efficiency Standards (MEES):</strong> The minimum EPC rating requirement is set to increase to band C for new tenancies, with existing tenancies following by 2028.</li>
                    <li><strong>Electrical Safety Standards:</strong> More rigorous testing protocols and shorter compliance cycles are being introduced.</li>
                    <li><strong>Carbon Monoxide Alarms:</strong> Extended requirements for CO alarms in all rooms with fuel-burning appliances, not just solid fuel.</li>
                    <li><strong>Fire Safety Measures:</strong> Enhanced requirements for multi-occupancy buildings, including more frequent risk assessments.</li>
                  </ul>
                  
                  <p>Non-compliance penalties are also increasing, with fines of up to £30,000 for serious breaches. Beyond financial penalties, non-compliant landlords may face restrictions on eviction rights and reputational damage.</p>
                  
                  <p>Staying ahead of these changes isn't just about avoiding penalties—it's about protecting your investment, your tenants, and your business reputation. Working with a comprehensive compliance partner like We Do Certs ensures you're always ahead of regulatory changes.</p>
                  
                  <div className="blog-action">
                    <a href="#contact" className="blog-action-btn">Get Compliance Ready</a>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="blog-post">
              <div className="blog-post-inner">
                <div className="blog-post-header">
                  <div className="blog-post-icon">🛑</div>
                  <h3 className="blog-post-title">Top 5 Compliance Mistakes Landlords Make — And How to Avoid Them</h3>
                </div>
                <p className="blog-post-excerpt">
                  Even experienced landlords can fall into compliance traps. Learn about the most common mistakes and how to protect yourself and your property portfolio.
                </p>
                <button className="blog-expand-btn" onClick={() => toggleBlogPost(2)}>
                  Read more <span className={`blog-expand-icon ${expandedBlogPost === 2 ? 'rotate' : ''}`}>↓</span>
                </button>
                <div className={`blog-content ${expandedBlogPost === 2 ? '' : 'blog-content-hidden'}`}>
                  <p>Property compliance isn't just a box-ticking exercise—it's a critical aspect of property management that protects both landlords and tenants. Here are the five most common compliance mistakes we see landlords make:</p>
                  
                  <h4>1. Missing Certificate Renewal Dates</h4>
                  <p>Many landlords lose track of when certificates expire, especially with different timeframes for different documents (1 year for gas safety, 5 years for electrical safety, 10 years for EPCs). Setting up a compliance calendar or working with a service that provides renewal reminders is essential.</p>
                  
                  <h4>2. Inadequate Record-Keeping</h4>
                  <p>Failing to maintain proper records of inspections, certificates, and tenant communications can be disastrous if disputes arise or if local authorities request documentation. Digital record-keeping systems are invaluable for maintaining compliance history.</p>
                  
                  <h4>3. DIY Compliance Checks</h4>
                  <p>Some landlords attempt to save money by conducting their own safety checks or using unqualified contractors. This is not only illegal but dangerous. Always use certified professionals for all compliance inspections.</p>
                  
                  <h4>4. Ignoring Tenant Reports</h4>
                  <p>Dismissing or delaying action on tenant-reported issues can escalate into serious compliance breaches. Establish clear communication channels and response protocols for maintenance requests.</p>
                  
                  <h4>5. Failing to Update After Property Modifications</h4>
                  <p>Many landlords don't realize that property modifications often require updated compliance certificates. Any significant changes to electrical systems, heating systems, or building structure should trigger a compliance review.</p>
                  
                  <p>The solution to all these issues? A systematic approach to compliance management, ideally with the support of a dedicated compliance partner who can track, schedule, and maintain all your certification needs.</p>
                  
                  <div className="blog-action">
                    <a href="#contact" className="blog-action-btn">Get Expert Compliance Support</a>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="blog-post">
              <div className="blog-post-inner">
                <div className="blog-post-header">
                  <div className="blog-post-icon">🧰</div>
                  <h3 className="blog-post-title">What to Expect During a Property Compliance Inspection: A Landlord's Guide</h3>
                </div>
                <p className="blog-post-excerpt">
                  Preparing for a compliance inspection? Here's a comprehensive walkthrough of what happens during different types of property assessments.
                </p>
                <button className="blog-expand-btn" onClick={() => toggleBlogPost(3)}>
                  Read more <span className={`blog-expand-icon ${expandedBlogPost === 3 ? 'rotate' : ''}`}>↓</span>
                </button>
                <div className={`blog-content ${expandedBlogPost === 3 ? '' : 'blog-content-hidden'}`}>
                  <p>Understanding what happens during compliance inspections helps landlords prepare effectively and ensures smooth, efficient assessments. Here's what to expect for the most common types of property compliance inspections:</p>
                  
                  <h4>Gas Safety Inspection (CP12)</h4>
                  <p>A Gas Safe registered engineer will examine all gas appliances, pipework, and flues. They'll check for gas leaks, ensure proper combustion, verify ventilation, and test safety devices. The inspection typically takes 30-60 minutes depending on the number of appliances.</p>
                  
                  <h4>Electrical Installation Condition Report (EICR)</h4>
                  <p>A qualified electrician will assess all fixed electrical installations including consumer units, wiring, sockets, light fittings, and earthing. This is more comprehensive than a gas check and may take 3-4 hours for an average property. Some power interruption will occur during testing.</p>
                  
                  <h4>Energy Performance Certificate (EPC) Assessment</h4>
                  <p>An accredited domestic energy assessor will measure your property, examine insulation, heating systems, windows, and lighting. They'll take photographs and gather data on construction and energy features. This non-invasive assessment typically takes 45-60 minutes.</p>
                  
                  <h4>Fire Risk Assessment</h4>
                  <p>A fire safety professional will inspect escape routes, fire detection systems, fire-fighting equipment, emergency lighting, and potential fire hazards. They'll review fire safety management procedures and documentation. For HMOs or larger properties, this can take several hours.</p>
                  
                  <h4>How to Prepare:</h4>
                  <ul>
                    <li>Ensure access to all areas of the property</li>
                    <li>Clear space around appliances and electrical installations</li>
                    <li>Have previous certificates available for reference</li>
                    <li>Inform tenants in advance (we can help with communication)</li>
                    <li>Address any known issues before the inspection</li>
                  </ul>
                  
                  <p>At We Do Certs, we coordinate all these inspections to minimize disruption and can often combine multiple assessments in a single visit. Our inspectors are trained to be thorough yet efficient, respecting your property and tenants throughout the process.</p>
                  
                  <div className="blog-action">
                    <a href="#contact" className="blog-action-btn">Schedule Your Inspection</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="blog-cta">
            <a href="#" className="blog-cta-btn">View All Articles</a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="faq-container">
          <div className="faq-header">
            <h2 className="faq-title">Frequently Asked Questions</h2>
            <div className="faq-underline"></div>
            <p className="faq-description">Find answers to the most common questions about our services and how we can help you stay compliant.</p>
          </div>
          <div className="faq-list">
            <div className="faq-item">
              <div className="faq-question" onClick={() => toggleFaq(1)}>
                <div className="faq-question-text">What services do you provide?</div>
                <div className={`faq-toggle-icon ${expandedFaq === 1 ? 'active' : ''}`}>+</div>
              </div>
              <div className={`faq-answer ${expandedFaq === 1 ? 'active' : ''}`}>
                <div className="faq-answer-content">
                  We offer a full range of property compliance certifications and related services, including:
                  <ul>
                    <li>Gas Safety Certificates</li>
                    <li>Electrical Safety Certificates (EICR)</li>
                    <li>Energy Performance Certificates (EPC)</li>
                    <li>Asbestos Surveys & Certificates</li>
                    <li>Fire Risk Assessments</li>
                    <li>Emergency Lighting Reports</li>
                    <li>Fire Safety & Smoke Alarm Compliance</li>
                    <li>Architectural Drawings</li>
                    <li>Floor Plans</li>
                  </ul>
                  Our all-in-one solution means you only need to deal with one trusted provider for all your compliance needs.
                </div>
              </div>
            </div>
            <div className="faq-item">
              <div className="faq-question" onClick={() => toggleFaq(2)}>
                <div className="faq-question-text">Who needs these certificates?</div>
                <div className={`faq-toggle-icon ${expandedFaq === 2 ? 'active' : ''}`}>+</div>
              </div>
              <div className={`faq-answer ${expandedFaq === 2 ? 'active' : ''}`}>
                <div className="faq-answer-content">
                  If you're a landlord, homeowner, estate agent, property manager, or developer, you are legally required to hold various safety and energy certificates to rent, sell, or manage properties. Our services ensure you're fully compliant with UK property laws and building regulations.
                </div>
              </div>
            </div>
            <div className="faq-item">
              <div className="faq-question" onClick={() => toggleFaq(3)}>
                <div className="faq-question-text">How quickly can you provide the certificates?</div>
                <div className={`faq-toggle-icon ${expandedFaq === 3 ? 'active' : ''}`}>+</div>
              </div>
              <div className={`faq-answer ${expandedFaq === 3 ? 'active' : ''}`}>
                <div className="faq-answer-content">
                  We offer fast turnaround times, with most certificates issued within 48–72 hours after the inspection has been carried out - tenants can be illusive and non-responsive at times. For urgent requests, we also offer same-day and next-day services, subject to availability and we may charge a little extra.
                </div>
              </div>
            </div>
            <div className="faq-item">
              <div className="faq-question" onClick={() => toggleFaq(4)}>
                <div className="faq-question-text">Can I book multiple services at once?</div>
                <div className={`faq-toggle-icon ${expandedFaq === 4 ? 'active' : ''}`}>+</div>
              </div>
              <div className={`faq-answer ${expandedFaq === 4 ? 'active' : ''}`}>
                <div className="faq-answer-content">
                  Yes! In fact, it's one of our most popular options. We can bundle services like gas, electric, EPCs, and fire risk assessments into one convenient visit—saving you time, money, and hassle.
                </div>
              </div>
            </div>
            <div className="faq-item">
              <div className="faq-question" onClick={() => toggleFaq(5)}>
                <div className="faq-question-text">Are your inspectors qualified and insured?</div>
                <div className={`faq-toggle-icon ${expandedFaq === 5 ? 'active' : ''}`}>+</div>
              </div>
              <div className={`faq-answer ${expandedFaq === 5 ? 'active' : ''}`}>
                <div className="faq-answer-content">
                  Absolutely. All our inspectors are fully qualified, accredited, and insured, with extensive experience in their specific field of compliance. We adhere strictly to UK regulations and industry standards.
                </div>
              </div>
            </div>
            <div className="faq-item">
              <div className="faq-question" onClick={() => toggleFaq(6)}>
                <div className="faq-question-text">How do I book a service?</div>
                <div className={`faq-toggle-icon ${expandedFaq === 6 ? 'active' : ''}`}>+</div>
              </div>
              <div className={`faq-answer ${expandedFaq === 6 ? 'active' : ''}`}>
                <div className="faq-answer-content">
                  Booking is quick and easy! You can:
                  <ul>
                    <li>Call us directly</li>
                    <li>Use our online booking form on our website</li>
                    <li>Email us your requirements</li>
                  </ul>
                  We'll confirm a convenient time and handle the rest.
                </div>
              </div>
            </div>
            <div className="faq-item">
              <div className="faq-question" onClick={() => toggleFaq(7)}>
                <div className="faq-question-text">What areas do you cover?</div>
                <div className={`faq-toggle-icon ${expandedFaq === 7 ? 'active' : ''}`}>+</div>
              </div>
              <div className={`faq-answer ${expandedFaq === 7 ? 'active' : ''}`}>
                <div className="faq-answer-content">
                  We provide coverage across the UK but 100% committed to the Midlands and more particularly Birmingham at present. Whether you have a single property or a large portfolio, we can accommodate your needs with local experts.
                </div>
              </div>
            </div>
            <div className="faq-item">
              <div className="faq-question" onClick={() => toggleFaq(8)}>
                <div className="faq-question-text">Do you offer pricing packages or discounts?</div>
                <div className={`faq-toggle-icon ${expandedFaq === 8 ? 'active' : ''}`}>+</div>
              </div>
              <div className={`faq-answer ${expandedFaq === 8 ? 'active' : ''}`}>
                <div className="faq-answer-content">
                  Yes. We offer competitive bundled pricing for multiple services and properties. Contact us for a personalised quote—no hidden fees, just clear, upfront costs.
                </div>
              </div>
            </div>
            <div className="faq-item">
              <div className="faq-question" onClick={() => toggleFaq(9)}>
                <div className="faq-question-text">What happens if my property fails a check?</div>
                <div className={`faq-toggle-icon ${expandedFaq === 9 ? 'active' : ''}`}>+</div>
              </div>
              <div className={`faq-answer ${expandedFaq === 9 ? 'active' : ''}`}>
                <div className="faq-answer-content">
                  If any issues are found, we'll provide a clear, detailed report outlining what needs to be addressed. We can often assist with recommendations or refer trusted professionals to carry out any necessary remedial work.
                </div>
              </div>
            </div>
            <div className="faq-item">
              <div className="faq-question" onClick={() => toggleFaq(10)}>
                <div className="faq-question-text">Why should I choose We Do Certs?</div>
                <div className={`faq-toggle-icon ${expandedFaq === 10 ? 'active' : ''}`}>+</div>
              </div>
              <div className={`faq-answer ${expandedFaq === 10 ? 'active' : ''}`}>
                <div className="faq-answer-content">
                  Because we make compliance easy. With one team, one booking, one invoice, and every certificate you need, you'll save time, reduce stress, and gain total peace of mind. We're fast, affordable, and 100% focused on keeping your property safe and legal.
                </div>
              </div>
            </div>
          </div>
          <div className="faq-cta">
            <h3 className="faq-cta-title">Still have questions?</h3>
            <p className="faq-cta-text">Our team is ready to help with any specific queries about your property compliance needs.</p>
            <a href="#contact" className="faq-cta-btn">Contact Us Today</a>
          </div>
        </div>
      </section>

      {/* Contact/CTA Section */}
      <section id="contact" className="contact-section">
        <div className="contact-container">
          <div className="contact-header">
            <h2 className="contact-title">We Do Certs</h2>
            <p className="contact-tagline">Book all your Landlord Property Compliance Certificates under one roof!</p>
            <p className="contact-intro">Let's discuss your goals and challenges over a quick email or call. Enter your details and we'll be in touch!</p>
          </div>
          <div className="contact-content">
            <div className="contact-form-container">
              <form className="contact-form">
                <div className="form-group">
                  <label className="form-label" htmlFor="name">Your Name</label>
                  <input type="text" id="name" className="form-input" required />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="email">Email Address</label>
                  <input type="email" id="email" className="form-input" required />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="phone">Phone Number</label>
                  <input type="tel" id="phone" className="form-input" />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="message">How can we help?</label>
                  <textarea id="message" className="form-textarea" rows={4} required></textarea>
                </div>
                <button type="submit" className="form-submit">Send Message</button>
              </form>
            </div>
            <div className="business-info">
              <h3 className="business-info-title">Better business.</h3>
              <div className="contact-info-item">
                <div className="contact-info-icon">📞</div>
                <div className="contact-info-text">
                  <strong>Sales</strong><br />
                  <a href="tel:01217516668" className="contact-info-link">0121 751 6668</a>
                </div>
              </div>
              <div className="contact-info-item">
                <div className="contact-info-icon">✉️</div>
                <div className="contact-info-text">
                  <strong>Email</strong><br />
                  <a href="mailto:info@wedocerts.co.uk" className="contact-info-link">info@wedocerts.co.uk</a>
                </div>
              </div>
            </div>
          </div>
          <div className="contact-footer">
            <div className="footer-links">
              <a href="#" className="footer-link">Privacy Policy</a>
              <a href="#" className="footer-link">Terms & Conditions</a>
            </div>
            <div className="copyright">© 2025 We Do Certs. All rights reserved.</div>
          </div>
        </div>
      </section>

      {/* Back to Top Button */}
      <div className={`back-to-top ${showBackToTop ? 'visible' : ''}`} onClick={scrollToTop}>
        ↑
      </div>
    </div>
  );
}

export default App;
